import * as React from 'react';
import { Modal, Button, Row, Col, Select, Icon, message, Checkbox, Form,
  Upload, Spin } from 'antd';
import { downloadFileData, getFileExtension } from 'src/utils/utils';
import moment from 'moment';
import { FormComponentProps } from 'antd/lib/form';
import { uploadToS3, getS3Links } from 'src/api/invoice';
import { REQUEST_STATUS } from '../../BulkRequest/BulkRequestUtils';
import { sendEvent, EventCategories } from 'src/utils/gaService';
import { downloadSampleBids, downloadSampleBidsShipper, getAllChargesForBulkBidsUpload, uploadBulkBids, uploadBulkBidsShipper } from 'src/api/inquiry';
import { styles } from './BidsBulkUploadModal.styles'
import { useGenericState } from 'src/Library/UseGenericState';
import { HocOptions } from 'src/components/hoc/GenericHoc.types';
import GenericHoc from 'src/components/hoc/GenericHoc';
import { tokens } from 'src/design-system';
import Asterisk from 'src/components/common/Asterisk';

const uploadText = (step) => {
  return step === 'Upload'? 'Upload Bids File' : (step === 'Prepare' ? 'Create Bidding Template' : `${step} Your Excel File`);
};

const getMandatoryOrPreviouslySelectedChargeCodes = (charges: BasicCharge[]): string[] => {
  const accumulator: string[] = [];
  return charges.reduce((acc, charge) => {
    if (charge.isPreviousCharge || charge.isMandatory){
      acc.push(charge.key);
    }
    return acc;
  }, accumulator);
}

interface BasicCharge {
  key: string;
  code: string;
  name: string;
  category: string;
  isMandatory: boolean;
  isPreviousCharge: boolean;
}


interface BidsBulkUploadModalProps extends FormComponentProps {
  classes: {
    [X in keyof ReturnType<typeof styles>]: string
  };
  isVisible: boolean;
  onClose: () => void;
  selectedShipperId: string;
  inquiryMode: string;
  isIncludeRespondedChecked: boolean;
  isShipper: boolean;
  selectedFF: string;
  getRequestObject: (e) => void;
}

const BidsBulkUploadModal = (props: BidsBulkUploadModalProps):
  React.ReactElement => {

  const {
    classes,
    isVisible,
    onClose,
    getRequestObject,
    form,
    selectedShipperId,
    isIncludeRespondedChecked,
    inquiryMode,
    isShipper,
    selectedFF
  } = props;

  const {
    getFieldDecorator,
    setFieldsValue,
    validateFieldsAndScroll,
    getFieldValue,
  } = form;

  type State = {
    isLoading: boolean,
    initialChargesLoading: boolean 
    freightCharges: BasicCharge[],
    originCharges: BasicCharge[],
    destinationCharges: BasicCharge[],
    selectAll: boolean,
    s3Link: string,
    fileName: string,
    isDownloading: boolean,
    isUploading: boolean,
  }
  const initialState: State = {
    isLoading: false,
    initialChargesLoading: false,
    freightCharges: [],
    originCharges: [],
    destinationCharges: [],
    selectAll: false,
    s3Link: '',
    fileName: '',
    isDownloading: false,
    isUploading: false,
  };
  
  const [state, setState] = useGenericState<State>(
    initialState
  );

  const {
    isLoading,
    s3Link,
    fileName,
    isDownloading,
    isUploading,
    initialChargesLoading,
    freightCharges,
    originCharges,
    destinationCharges,
  } = state;


  React.useEffect(() => {
    async function fetchChargesData() {
      try {
        setState({
          initialChargesLoading: true
        })
        const response = await getAllChargesForBulkBidsUpload({shipperId: selectedShipperId});
        if (response.isSuccess && response.data !== undefined)
        {
          const {
            freightCharges: freightChargesData,
            originCharges: originChargesData,
            destinationCharges : destinationChargesData,
          }: {
            freightCharges: BasicCharge[], 
            originCharges: BasicCharge[], 
            destinationCharges: BasicCharge[]
          } = response.data;
          setState({
            freightCharges: freightChargesData,
            originCharges: originChargesData,
            destinationCharges: destinationChargesData,
            initialChargesLoading: false
          });

          setFieldsValue({
            fieldFreightChargesCodeList : getMandatoryOrPreviouslySelectedChargeCodes(freightChargesData),
            fieldDestinationChargesCodeList : getMandatoryOrPreviouslySelectedChargeCodes(destinationChargesData),
            fieldOriginChargesCodeList : getMandatoryOrPreviouslySelectedChargeCodes(originChargesData)
          })

        } else {
          message.error(response.errorMessage);
        }
      } catch (err){
        message.error(err.message);
      }
    }
    
    fetchChargesData();

  }, [])

  const s3LinkRequest = async ({ file }): Promise<void> => {
    setState({
      isUploading: true,
      fileName: file.name,
    });
    try {
      const extension = getFileExtension(file.name);
      const response = await getS3Links({
        extension,
        fileName: file.name,
        document_type: file.type,
        initialPath: 'inquirybulk-bids',
      });
      let s3Link: string = '';
      if (response.isSuccess) {
        const upload = await uploadToS3(response.data.post, extension, file);
        if (upload.isSuccess) s3Link = response.data.get;
      } else {
        message.error(response.errorMessage);
      }
      setState({
        s3Link,
        isUploading: false,
      });
    } catch (err){
      message.error(err.message);
      setState({
        s3Link: '',
        isUploading: false,
      });
    }
  };

  const handleSampleLinkDownload = async () => {
    validateFieldsAndScroll(async (err, values) => {
      if (err) {
        message.error('Error: Kindly recheck form details');
        return;
      }

      setState({
        isDownloading: true,
      });
      
      const chargeCodeList = {
        freightCharges: values.fieldFreightChargesCodeList,
        originCharges: values.fieldOriginChargesCodeList,
        destinationCharges: values.fieldDestinationChargesCodeList
      }

      const commonParams = {
        mode: inquiryMode,
        includeRespondedInquiries: isIncludeRespondedChecked,
        shipperOrgId: selectedShipperId,
        ...chargeCodeList
      }

      try {
        const response = isShipper ? await downloadSampleBidsShipper({
          ...commonParams,
          ffOrgId: selectedFF
        }) : await downloadSampleBids(
          commonParams
          );

      if (!response.isSuccess) {
        sendEvent(
          EventCategories.INQUIRY_BIDDING,
          'Upload Bids - Download Sample File (Failed)',
        );

        message.error(response.errorMessage);
      } else {
        sendEvent(
          EventCategories.INQUIRY_BIDDING,
          'Upload Bids - Download Sample File',
        );

        downloadFileData(response.data,
          response.filename ||
          `sample-bid-${
            moment().format('DD-MM-YYYY')}.xlsx`, true);

        message.success('Downloaded successfully');
      }

      } catch (err){
        message.error(err.message);
      }

      setState({
        isDownloading: false,
      });

    })
  }

  const renderChargesSelection = (chargeName: string, chargesValues: BasicCharge[]) : React.ReactElement => {
    return (
      <Form.Item>
        {getFieldDecorator(`field${chargeName}CodeList`, {
          rules: [{ required: true, message: 'Charge(s) is Required'}],
          valuePropName: 'value',
        })
        (
          <Select
            mode="multiple"
            showArrow
            placeholder="Select"
            showSearch
            className={classes.select}
            optionLabelProp="label"
            allowClear={false}
            getPopupContainer={(trigger: any) => trigger.parentNode}
            filterOption={(inputValue, option) => {
              if (!inputValue) {
                return true;
              }
              const optionval = option.props['data-name'] ?
                option.props['data-name'].trim().toLowerCase() : '';
              
              const optioncode = option.props.value ?
                option.props.value.toString().trim().toLowerCase() : '';
              return optionval.includes(inputValue.trim().toLowerCase()) 
                || optioncode.includes(inputValue.trim().toLowerCase());
            }}
          >
            {chargesValues?.map((item) => {
              return <Select.Option disabled={item.isMandatory}
                key={item.key} data-name={item.name} value={item.key} label={item.name + (item.isMandatory ? '* ' : ' ') + `(${item.code})`} >
                <Checkbox
                  checked={
                    getFieldValue(`field${chargeName}CodeList`)?.includes(item.key)
                    || item.isMandatory}
                  style={{ marginRight: '10px' }}></Checkbox>
                {item.name}{item.isMandatory && <Asterisk />} {`(${item.code})`}
              </Select.Option>;
            })}
          </Select>,
        )}
      </Form.Item>
    );
  };

  const renderOptions = (title, options) : React.ReactElement => {
    return (
      <Row>
        <Col span={4} className={classes.formLabel}>{title}</Col>
        <Col span={20} className={classes.form}>{options}</Col>
      </Row>
    );
  };

  const renderUpload = () : React.ReactElement => {
    return (
      <Col span={9} style={{ paddingLeft: '20px' }}>
        <div className={classes.uploadStepText}>{uploadText('Upload')}</div>
        {renderUploadDiv()}
        {fileName && <div className={classes.fileName}>{fileName}
          {!isLoading && <Icon type="close" style={{ paddingLeft: '10px', color: 'red' }}
            onClick={() => setState({
              fileName: '',
              s3Link: '',
            })}/>}</div>}
      </Col>
    )
  }

  const getTabContent = () : React.ReactElement => {
    return (
      <Col span={15} className={classes.downloadFileCol}>
        <div className={classes.uploadStepText}>{uploadText('Prepare')}</div>
        <div style={{ marginBottom: '30px' }}>
          {renderOptions('Freight Charges*', renderChargesSelection('FreightCharges', freightCharges))}
          {renderOptions('Origin Charges*', renderChargesSelection('OriginCharges', originCharges))}
          {renderOptions('Destination Charges*', renderChargesSelection('DestinationCharges', destinationCharges))}
        </div>

        <div className={classes.downloadFileText}
          onClick={() => handleSampleLinkDownload()}>
          <Icon type="download" style={{ marginRight: '5px' }}></Icon>
            <span>Download Bids Template
              {isDownloading &&
                <Spin size="small" style={{ marginLeft: 8 }} />
              }
            </span> 
        </div>
      </Col>
    );
  };

  const handleUpload = async (): Promise<void> => {
    if (!fileName) {
      message.error('Upload a file and try again');
      return;
    }
    validateFieldsAndScroll(async (err, values) => {
      if (err) {
        return;
      }   
      setState({
        isLoading: true,
      });
      const extension = getFileExtension(fileName)?.toLowerCase();
      const fileNameWithoutExt = fileName?.replace(`.${extension}`, '');

      const commonParams = {
        mode: inquiryMode,
        shipperId: selectedShipperId,
        fileName: `${fileNameWithoutExt?.replace(/ /g, '_')}.${extension}`,
        s3FileUrl: s3Link,
      }
      try {
        const response = isShipper ? await uploadBulkBidsShipper({
          ...commonParams,
          ffId: selectedFF
        }) : await uploadBulkBids(
          commonParams
        );

        if (response.data) {
        getRequestObject({
          fileName,
          id: response.data?.data?.requestId,
            status: REQUEST_STATUS.PENDING,
        });
          message.success('Uploaded Successfully');
          handleModalClose();
        } else {
          message.error(response.errorMessage)
        }
      } catch (err){
        message.error(err.message);
      }

      setState({
        isLoading: false,
      });
    });
  };

  const handleModalClose = () : void => {
    setState({
      fileName: '',
      // selectAll: false,
    });
    onClose();
  };

  const renderFooter = () : React.ReactElement => {
    return (
      <React.Fragment>
        <Button size="large" style={{ backgroundColor: '#F2F4F6', border: '1px solid #DDE0E5',
          color: '#424242' }} onClick={handleModalClose}>Cancel</Button>
        <Button size="large" className={classes.uploadButton}
          disabled={!(s3Link && fileName)}
          loading={isLoading} onClick={() => handleUpload()}>Upload and Send</Button>{isLoading}
      </React.Fragment>
    );
  };

  const renderUploadDiv = () : React.ReactElement => {
    return (
      <Upload.Dragger disabled={isUploading}
        showUploadList={false}
        multiple={false}
        accept={'.xlsx, .xls, .csv'}
        customRequest={file => s3LinkRequest(file)}
        className={classes.uploadStyle}
        >
          <span className={classes.uploadText}>
            Upload Excel File</span>
            <Icon type="inbox" style={{ fontSize: '4.3em',
              color: tokens.colors.primary }} />
          <span className={classes.dragnDropText}>
            Drag and drop to upload or<br/>
            <span style={{ color: tokens.colors.primary }}>browse</span> to choose file</span>
          {isUploading && <Spin />}
      </Upload.Dragger>
    );
  };

  return (
    <Modal title="Bulk Upload"
      visible={isVisible}
      onCancel={handleModalClose}
      className={classes.modal}
      destroyOnClose
      footer={renderFooter()}
    >
      <div style={{ paddingRight: 0 }} className={classes.tabStyle}>
          {initialChargesLoading ?
          <div className={classes.centerDiv} >
            <Spin/>
          </div> 
             :
        <Row className={classes.tabContent}>
          {getTabContent()}
          {renderUpload()}
        </Row>
          }
      </div>
    </Modal>
  );
};

const options: HocOptions = {
  connectForm: {
    useForm: true
  },
  connectJss: {
    useJss: true,
    styleSheet: styles,
  }
}

export default GenericHoc(options)(BidsBulkUploadModal);
